import { ExternalCalendarFeedLinksDto, ExternalCalendarFeedStatusDto } from "@lib/infrastructure";

type LoadingState = {
  kind: "loading";
  request: "status" | "links";
  hasStarted: boolean;
  links: ExternalCalendarFeedLinksDto | null;
  canRotate: boolean;
  showRotateNotice: boolean;
};

type ReadyNotStartedState = {
  kind: "ready-not-started";
};

type ReadyStartedState = {
  kind: "ready-started";
  links: ExternalCalendarFeedLinksDto;
  canRotate: boolean;
  showRotateNotice: boolean;
  isRotating: boolean;
};

type ErrorState = {
  kind: "error";
  errorKey: "auth" | "generic";
  hasStarted: boolean;
  links: ExternalCalendarFeedLinksDto | null;
  canRotate: boolean;
  showRotateNotice: boolean;
};

export type ExternalCalendarFeedState = LoadingState | ReadyNotStartedState | ReadyStartedState | ErrorState;

type ExternalCalendarFeedAction =
  | { type: "start-loading-status" }
  | { type: "start-loading-links" }
  | { type: "status-loaded"; payload: ExternalCalendarFeedStatusDto }
  | { type: "links-loaded"; payload: ExternalCalendarFeedLinksDto }
  | { type: "load-failed"; payload: { errorKey: "auth" | "generic" } }
  | { type: "start-rotating" }
  | { type: "rotate-success"; payload: ExternalCalendarFeedLinksDto }
  | { type: "rotate-failed" };

export const getExternalCalendarFeedStateSnapshot = (state: ExternalCalendarFeedState) => {
  if (state.kind === "ready-started") {
    return {
      hasStarted: true,
      links: state.links,
      canRotate: state.canRotate,
      showRotateNotice: state.showRotateNotice,
    };
  }

  if (state.kind === "loading" || state.kind === "error") {
    return {
      hasStarted: state.hasStarted,
      links: state.links,
      canRotate: state.canRotate,
      showRotateNotice: state.showRotateNotice,
    };
  }

  return {
    hasStarted: false,
    links: null,
    canRotate: false,
    showRotateNotice: false,
  };
};

export const initialExternalCalendarFeedState: ExternalCalendarFeedState = {
  kind: "loading",
  request: "status",
  hasStarted: false,
  links: null,
  canRotate: false,
  showRotateNotice: false,
};

export const externalCalendarFeedReducer = (
  state: ExternalCalendarFeedState,
  action: ExternalCalendarFeedAction,
): ExternalCalendarFeedState => {
  switch (action.type) {
    case "start-loading-status": {
      const snapshot = getExternalCalendarFeedStateSnapshot(state);
      return {
        kind: "loading",
        request: "status",
        ...snapshot,
      };
    }

    case "start-loading-links": {
      const snapshot = getExternalCalendarFeedStateSnapshot(state);
      return {
        kind: "loading",
        request: "links",
        ...snapshot,
      };
    }

    case "status-loaded": {
      const status = action.payload;

      if (status.hasLink && status.url && status.webcalUrl) {
        return {
          kind: "ready-started",
          links: {
            url: status.url,
            webcalUrl: status.webcalUrl,
          },
          canRotate: status.canRotate,
          showRotateNotice: false,
          isRotating: false,
        };
      }

      return {
        kind: "ready-not-started",
      };
    }

    case "links-loaded": {
      return {
        kind: "ready-started",
        links: action.payload,
        canRotate: true,
        showRotateNotice: getExternalCalendarFeedStateSnapshot(state).showRotateNotice,
        isRotating: false,
      };
    }

    case "load-failed": {
      const snapshot = getExternalCalendarFeedStateSnapshot(state);
      return {
        kind: "error",
        errorKey: action.payload.errorKey,
        ...snapshot,
      };
    }

    case "start-rotating": {
      if (state.kind !== "ready-started") return state;

      return {
        ...state,
        isRotating: true,
      };
    }

    case "rotate-success": {
      if (state.kind !== "ready-started") return state;

      return {
        kind: "ready-started",
        links: action.payload,
        canRotate: true,
        isRotating: false,
        showRotateNotice: true,
      };
    }

    case "rotate-failed": {
      if (state.kind !== "ready-started") return state;

      return {
        ...state,
        isRotating: false,
      };
    }

    default:
      return state;
  }
};
